class CountdownTimer extends HTMLElement {
  /**
   * Target date for the countdown
   *
   * @type {number}
   */
  targetDate;

  /**
   * Countdown elements
   *
   * @type {Object}
   */
  countdownElements;

  constructor() {
    super();

    // Initialize countdownElements directly
    this.countdownElements = {
      daysTens: this.querySelector("#times_day_tens"),
      daysOnes: this.querySelector("#times_day_ones"),
      hoursTens: this.querySelector("#times_hour_tens"),
      hoursOnes: this.querySelector("#times_hour_ones"),
      minutesTens: this.querySelector("#times_minute_tens"),
      minutesOnes: this.querySelector("#times_minute_ones"),
      secondsTens: this.querySelector("#times_second_tens"),
      secondsOnes: this.querySelector("#times_second_ones"),
      daysOnesSymbol: this.querySelector("#times_day_ones_symbol"),
      countdownText: this.querySelector(".components-countdown-text"),
      countdownItem: this.querySelector(".components-countdown-time_items"),
    };

    this.targetDate = this.getSafeTimestamp(
      this.getAttribute("data-date"),
      this.getAttribute("data-timezone")
    );
    this.updateCountdown();

    this.interval = setInterval(() => this.updateCountdown(), 1000);
  }

  getSafeTimestamp(dateStr, timeZone) {
    if (!dateStr) return Date.now();

    let str = dateStr.trim().replace(/\//g, "-");
    str = str.replace(/ ?[A-Z]{3,4}$/, "").trim();

    if (/[zZ]$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str)) {
      const parsed = Date.parse(str);
      return Number.isNaN(parsed) ? Date.now() : parsed;
    }

    const match = str.match(
      /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2})(?::(\d{1,2})(?::(\d{1,2}))?)?)?$/
    );
    if (!match || !timeZone) {
      const parsed = Date.parse(dateStr);
      return Number.isNaN(parsed) ? Date.now() : parsed;
    }

    return this.zonedWallTimeToUtc(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
      Number(match[4] || 0),
      Number(match[5] || 0),
      Number(match[6] || 0),
      timeZone
    );
  }

  getTimeZoneOffsetMs(date, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);

    const value = {};
    parts.forEach((part) => {
      if (part.type !== "literal") value[part.type] = part.value;
    });

    let hour = Number(value.hour);
    if (hour === 24) hour = 0;

    const asUTC = Date.UTC(
      Number(value.year),
      Number(value.month) - 1,
      Number(value.day),
      hour,
      Number(value.minute),
      Number(value.second)
    );

    return asUTC - date.getTime();
  }

  zonedWallTimeToUtc(year, month, day, hour, minute, second, timeZone) {
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
    let offset = this.getTimeZoneOffsetMs(new Date(utcGuess), timeZone);
    let utc = utcGuess - offset;
    const offset2 = this.getTimeZoneOffsetMs(new Date(utc), timeZone);
    if (offset2 !== offset) {
      utc = utcGuess - offset2;
    }
    return utc;
  }

  updateCountdown() {
    const now = new Date().getTime();
    const timeRemaining = this.targetDate - now;

    if (timeRemaining <= 0) {
      // Countdown is over
      this.resetCountdown();
    } else {
      // Calculate remaining days, hours, minutes, and seconds
      const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor(
        (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

      // Update countdown elements
      this.updateElement(
        this.countdownElements.daysTens,
        days > 0 ? Math.floor(days / 10).toString() : "0"
      );
      this.updateElement(
        this.countdownElements.daysOnes,
        (days % 10).toString()
      );
      this.updateElement(
        this.countdownElements.hoursTens,
        Math.floor(hours / 10).toString()
      );
      this.updateElement(
        this.countdownElements.hoursOnes,
        (hours % 10).toString()
      );
      this.updateElement(
        this.countdownElements.minutesTens,
        Math.floor(minutes / 10).toString()
      );
      this.updateElement(
        this.countdownElements.minutesOnes,
        (minutes % 10).toString()
      );
      this.updateElement(
        this.countdownElements.secondsTens,
        Math.floor(seconds / 10).toString()
      );
      this.updateElement(
        this.countdownElements.secondsOnes,
        (seconds % 10).toString()
      );

      // Hide daysTensElement if days is 0
      if (this.countdownElements.daysTens) {
        this.countdownElements.daysTens.style.display =
          days > 0 ? "inline-block" : "none";
      }
      if (this.countdownElements.daysOnes) {
        this.countdownElements.daysOnes.style.display =
          days > 0 ? "inline-block" : "none";
      }
      if (days == 0 && this.countdownElements.daysOnesSymbol) {
        this.countdownElements.daysOnesSymbol.style.display = "none";
      }
    }
  }

  updateElement(element, value) {
    if (element) {
      element.textContent = value;
    }
  }

  resetCountdown() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    const wrapper = this.closest(".announcement-bar_countdown");
    if (wrapper) {
      wrapper.style.display = "none";
      this.style.display = "none";
      return;
    }

    for (const key in this.countdownElements) {
      this.updateElement(this.countdownElements[key], "0");
    }
    if (this.countdownElements.countdownItem) {
      this.countdownElements.countdownItem.style.display = "none";
    }
    if (
      this.countdownElements.countdownText &&
      this.countdownElements.countdownText.textContent.trim()
    ) {
      this.updateElement(
        this.countdownElements.countdownText,
        "Oops! It's over!"
      );
    }
  }
}

// Use custom element tag name without a dot
if (!customElements.get("countdown-timer")) {
  customElements.define("countdown-timer", CountdownTimer);
}
